import React, { useState } from "react";
import { Box, Modal } from "@mui/material";
import toast from "react-hot-toast";
import { getWebmUrl } from "../../utils/downloadHelpers";
import { formatVideoName } from "../../pages/admin/videos/hooks/useFetchVideos";
import { fancyTimeFormat } from "../../pages/admin/videos/adminVideosV2/helper";

interface Props {
  open: boolean;
  onClose: () => void;
  video: any | null;
}

/** §08 chrome (title bar, --player plate, meta footer) around the existing
 * multi-source + error-handling player internals. */
export default function VideoPlayerDialog({ open, onClose, video }: Props) {
  const [videoErrorShown, setVideoErrorShown] = useState(false);

  if (!video) return null;

  const videoLocation = video.location || video.reviewS3File?.location || "";
  const webmUrl = getWebmUrl(videoLocation);
  const title = formatVideoName(video?.title || video?.name || video?.key || "") || "Video";
  const meta = [
    video?.version ? `v${video.version}` : "",
    video?.aspect_ratio || "",
    video?.length ? fancyTimeFormat(video.length) : "",
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Modal open={open} onClose={onClose} className="flex items-center justify-center p-4 sm:p-8">
      <Box
        className="w-full max-w-[880px] bg-surface border border-border-token rounded-xl overflow-hidden outline-none"
        style={{ boxShadow: "var(--sh-2)" }}
      >
        <Box className="flex items-center gap-2.5 px-4 py-3 border-b border-border-token">
          <b className="flex-1 min-w-0 truncate text-[13.5px] font-bold text-text-1">{title}</b>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="w-7 h-7 rounded-lg bg-surface-2 text-text-1 flex items-center justify-center shrink-0"
          >
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M6 6 18 18M18 6 6 18" />
            </svg>
          </button>
        </Box>
        <Box className="bg-player">
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video
            controls
            autoPlay
            playsInline
            preload="metadata"
            style={{ width: "100%", maxHeight: "70vh", display: "block" }}
            crossOrigin="anonymous"
            onError={(e: any) => {
              const vid = e.target as HTMLVideoElement;
              if (vid.error?.code === vid.error?.MEDIA_ERR_SRC_NOT_SUPPORTED) {
                if (vid.readyState === 0 && !videoErrorShown) {
                  setVideoErrorShown(true);
                  toast.error("Failed to load video. Please try downloading it instead.");
                }
              } else if (vid.error?.code === vid.error?.MEDIA_ERR_NETWORK && !videoErrorShown) {
                setVideoErrorShown(true);
                toast.error("Network error loading video. Please try again.");
              }
            }}
            onLoadStart={() => setVideoErrorShown(false)}
            onCanPlay={() => setVideoErrorShown(false)}
          >
            {videoLocation && (
              <source
                src={videoLocation}
                type={`video/${videoLocation.substring(videoLocation.lastIndexOf(".") + 1) || "mp4"}`}
              />
            )}
            {webmUrl && webmUrl !== videoLocation && <source src={webmUrl} type="video/webm" />}
            Your browser does not support the video tag.
          </video>
        </Box>
        {meta && (
          <Box className="flex items-center px-4 py-3">
            <span className="text-xs text-text-2 tabular-nums">{meta}</span>
          </Box>
        )}
      </Box>
    </Modal>
  );
}
